import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import type { CustomFieldRow } from "@shared/types";

type Value = string | number | boolean | string[] | null;

/**
 * Renders the project's custom fields as editable controls on a task and
 * upserts each value as it changes. Hidden entirely when the project has no
 * custom fields defined.
 */
export function CustomFieldsEditor({
  taskId,
  projectId,
}: {
  taskId: string;
  projectId: string;
}) {
  const qc = useQueryClient();
  const fieldsQ = useQuery({
    queryKey: queryKeys.customFields(projectId),
    queryFn: () => api.listCustomFields(projectId),
  });
  const valuesQ = useQuery({
    queryKey: queryKeys.customFieldValues(projectId),
    queryFn: () => api.customFieldValues(projectId),
  });

  const set = useMutation({
    mutationFn: ({ fieldId, value }: { fieldId: string; value: Value }) =>
      api.setCustomFieldValue(taskId, fieldId, value),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: queryKeys.customFieldValues(projectId),
      }),
  });

  const fields = fieldsQ.data?.fields ?? [];
  if (fields.length === 0) return null;

  const valueFor = (fieldId: string): Value => {
    const v = valuesQ.data?.values.find(
      (x) => x.fieldId === fieldId && x.taskId === taskId,
    );
    return v ? v.value : null;
  };

  return (
    <section className="blk" aria-label="Custom fields">
      <h3 className="blk-h">Custom fields</h3>
      <div className="cf-edit-grid">
        {fields.map((f) => (
          <div key={f.id} className="cf-edit-row">
            <label className="cf-edit-label" htmlFor={`cf-${f.id}`}>
              {f.name}
            </label>
            <FieldControl
              field={f}
              value={valueFor(f.id)}
              onChange={(value) => set.mutate({ fieldId: f.id, value })}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

function FieldControl({
  field,
  value,
  onChange,
}: {
  field: CustomFieldRow;
  value: Value;
  onChange: (v: Value) => void;
}) {
  const id = `cf-${field.id}`;
  switch (field.type) {
    case "number":
      return (
        <input
          id={id}
          className="input"
          type="number"
          value={value == null ? "" : String(value)}
          onChange={(e) =>
            onChange(e.target.value === "" ? null : Number(e.target.value))
          }
        />
      );
    case "date":
      return (
        <input
          id={id}
          className="input"
          type="date"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
    case "checkbox":
      return (
        <input
          id={id}
          type="checkbox"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
        />
      );
    case "select":
      return (
        <select
          id={id}
          className="select"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value || null)}
        >
          <option value="">—</option>
          {(field.options ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    case "multi_select": {
      const selected = Array.isArray(value) ? value : [];
      return (
        <div className="cf-multi">
          {(field.options ?? []).map((o) => {
            const on = selected.includes(o);
            return (
              <button
                key={o}
                type="button"
                className={`chip-btn${on ? " on" : ""}`}
                onClick={() =>
                  onChange(
                    on ? selected.filter((x) => x !== o) : [...selected, o],
                  )
                }
              >
                {o}
              </button>
            );
          })}
        </div>
      );
    }
    default:
      return (
        <input
          id={id}
          className="input"
          type="text"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
  }
}
